import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getDockConfigSignature } from './utils';

/**
 * @typedef {object} MobileIslandDockAction
 * @property {string} key
 * @property {string|Array} icon
 * @property {string} label
 * @property {() => void} onClick
 * @property {boolean} [active]
 * @property {boolean} [disabled]
 * @property {boolean} [loading]
 * @property {boolean} [hidden]
 * @property {'default'|'success'|'danger'} [tone]
 * @property {number} [badge]
 * @property {string} [ariaControls]
 * @property {boolean} [ariaExpanded]
 */

/**
 * @typedef {object} MobileIslandDockLeadItem
 * @property {string} key
 * @property {string|Array} [icon]
 * @property {import('react').ElementType} [iconComponent]
 * @property {string} label
 * @property {() => void} [onClick]
 */

/**
 * @typedef {object} MobileIslandDockSearchConfig
 * @property {string} inputId
 * @property {string} [name]
 * @property {string} [appliedValue] 已应用关键词（收起时同步到输入草稿）
 * @property {(value: string, event?: Event) => void} [onSubmit] 用户按键盘「搜索/Enter」时提交
 * @property {() => void} [onDismiss] 关闭搜索：清空并取消
 * @property {string} [placeholder]
 */

/**
 * @typedef {object} MobileIslandDockConfig
 * @property {string} id
 * @property {MobileIslandDockLeadItem[]} leadItems 左岛（页面按钮）
 * @property {MobileIslandDockAction[]} [actions] 中岛（快速链接 / 页面操作）
 * @property {MobileIslandDockSearchConfig | null} [search] 右岛（全局 / 页面搜索）
 */

const MobileIslandDockRegistrationContext = createContext(null);
const MobileIslandDockConfigContext = createContext(null);

export const MobileIslandDockProvider = ({ children }) => {
	const [dockConfig, setDockConfig] = useState(null);
	const dockConfigRef = useRef(null);

	const registerMobileIslandDock = useCallback((config) => {
		dockConfigRef.current = config;
		setDockConfig((prev) => {
			if (getDockConfigSignature(prev) === getDockConfigSignature(config)) {
				return prev;
			}
			return config;
		});
	}, []);

	const clearMobileIslandDock = useCallback((id) => {
		if (id != null && dockConfigRef.current?.id !== id) {
			return;
		}
		dockConfigRef.current = null;
		setDockConfig(null);
	}, []);

	const registrationValue = useMemo(
		() => ({
			registerMobileIslandDock,
			clearMobileIslandDock,
		}),
		[registerMobileIslandDock, clearMobileIslandDock],
	);

	return (
		<MobileIslandDockRegistrationContext.Provider value={registrationValue}>
			<MobileIslandDockConfigContext.Provider value={dockConfig}>{children}</MobileIslandDockConfigContext.Provider>
		</MobileIslandDockRegistrationContext.Provider>
	);
};

MobileIslandDockProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export const useMobileIslandDock = () => {
	const registration = useContext(MobileIslandDockRegistrationContext);
	const dockConfig = useContext(MobileIslandDockConfigContext);
	if (!registration) {
		throw new Error('useMobileIslandDock must be used within MobileIslandDockProvider');
	}
	return {
		dockConfig,
		...registration,
	};
};

/** 页面挂载时注册三岛配置，卸载时清除（layout 阶段注销，避免与下一页注册竞态） */
export const useRegisterMobileIslandDock = (dockConfig) => {
	const registration = useContext(MobileIslandDockRegistrationContext);
	const dockIdRef = useRef(dockConfig?.id);

	useLayoutEffect(() => {
		if (!registration) {
			throw new Error('useRegisterMobileIslandDock must be used within MobileIslandDockProvider');
		}
		const { registerMobileIslandDock, clearMobileIslandDock } = registration;
		dockIdRef.current = dockConfig?.id;
		registerMobileIslandDock(dockConfig);
		return () => clearMobileIslandDock(dockIdRef.current);
	}, [dockConfig, registration]);
};
